import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useApiClient } from '@/api/ApiClientProvider'
import { activationStatus, CAMPAIGN_STATUS_LABELS, OBJECTIVE_LABELS, type CampaignDetail, type CampaignPatch, type CampaignStatus } from '@/api/campaigns'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, EmptyState, LoadingBlock } from '@/design'
import { usePermissions } from '@/lib/access'
import { formatDateKey, toDateKey } from '@/lib/filters'
import { usePageFreshness } from '@/lib/freshness'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import { CampaignStatusBadge, formatDateRange } from './components'
import { ActivityCard, AudienceCard, AutomationCard, ChannelsBudgetCard, CreativeCard } from './detail/EditCards'
import { PerformanceSection } from './detail/PerformanceSection'
import { TrackingCard } from './detail/TrackingCard'
import { errorMessage, useCampaign, useCampaignMutation, useCampaignPerformance, useMoney } from './hooks'
import './campaigns.css'

/** The status changes that make sense from each status, as button labels. */
const TRANSITIONS: Record<CampaignStatus, Array<{ to: CampaignStatus; label: string }>> = {
  draft: [
    { to: 'active', label: 'Launch' },
    { to: 'archived', label: 'Archive' },
  ],
  scheduled: [
    { to: 'active', label: 'Start now' },
    { to: 'paused', label: 'Pause' },
    { to: 'archived', label: 'Archive' },
  ],
  active: [
    { to: 'paused', label: 'Pause' },
    { to: 'completed', label: 'Complete' },
  ],
  paused: [
    { to: 'active', label: 'Resume' },
    { to: 'completed', label: 'Complete' },
    { to: 'archived', label: 'Archive' },
  ],
  completed: [{ to: 'archived', label: 'Archive' }],
  archived: [],
}

function StatusActions({ detail, canManage }: { detail: CampaignDetail; canManage: boolean }) {
  const client = useApiClient()
  const { campaign } = detail
  const mutation = useCampaignMutation((patch: CampaignPatch) => client.updateCampaign(campaign.id, patch))
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="camp-status-actions">
      <CampaignStatusBadge status={campaign.status} />
      {canManage
        ? TRANSITIONS[campaign.status].map((transition) => (
            <button
              key={transition.to}
              type="button"
              className="ci-button"
              data-variant={transition.to === 'active' ? 'primary' : undefined}
              disabled={mutation.isPending || (transition.to === 'active' && detail.channels.length === 0)}
              onClick={() => {
                setError(null)
                // Launching or resuming before the start date schedules the campaign, as the create flow does.
                const status =
                  transition.to === 'active' ? activationStatus(campaign.status, campaign.startsAt, toDateKey(new Date())) : transition.to
                mutation
                  .mutateAsync([{ status }])
                  .catch((caught: unknown) => setError(errorMessage(caught, `Could not change the status to ${CAMPAIGN_STATUS_LABELS[transition.to].toLowerCase()}.`)))
              }}
            >
              {transition.label}
            </button>
          ))
        : null}
      {error ? (
        <p role="alert" className="camp-error">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/**
 * One campaign: its status, what it returned, every channel online and offline
 * side by side with a drill-down each, and its channels, budget, tracking,
 * creative, audience, automation and activity — editable by those who may.
 */
export function CampaignDetailPage({ route }: { route: RouteDef }) {
  const { id } = useParams<{ id: string }>()
  const search = useFilterSearch()
  const money = useMoney()
  const access = usePermissions()
  const canManage = access.can('campaigns.manage')
  const detail = useCampaign(id)
  const found = detail.data?.data ?? null
  const performance = useCampaignPerformance(id, Boolean(found))
  usePageFreshness(detail.data?.meta)

  if (detail.isPending) {
    return (
      <SectionFrame route={route}>
        <LoadingBlock rows={8} label="Loading campaign" />
      </SectionFrame>
    )
  }

  if (detail.isError || !found) {
    return (
      <SectionFrame route={route}>
        <Card>
          {detail.isError ? (
            <EmptyState variant="outage" description="This campaign could not be loaded. Try again shortly." />
          ) : (
            <EmptyState
              variant="no-results"
              title="Campaign not found"
              description="It may have been removed, or the link is out of date."
              action={
                <Link className="ci-button" to={{ pathname: '/marketing/campaigns', search }}>
                  All campaigns
                </Link>
              }
            />
          )}
        </Card>
      </SectionFrame>
    )
  }

  const { campaign } = found
  const objective = campaign.customObjective ?? OBJECTIVE_LABELS[campaign.objective]
  const dates = formatDateRange(campaign.startsAt, campaign.endsAt, (key) => formatDateKey(key, true))

  return (
    <SectionFrame
      route={route}
      title={campaign.name}
      description={[objective, dates, campaign.description].filter(Boolean).join(' · ')}
      actions={<StatusActions detail={found} canManage={canManage} />}
    >
      <div className="ci-stack">
        <PerformanceSection query={performance} money={money} />
        <ChannelsBudgetCard key={`budget-${campaign.updatedAt}`} detail={found} canManage={canManage} money={money} />
        <TrackingCard detail={found} canManage={canManage} />
        <CreativeCard detail={found} canManage={canManage} />
        <AudienceCard detail={found} canManage={canManage} />
        <AutomationCard detail={found} canManage={canManage} />
        <ActivityCard detail={found} />
      </div>
    </SectionFrame>
  )
}
